import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { SeoHead } from "../components/SeoHead";
import { Loader2, Sparkles, ChevronRight, BookOpen, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ColumnsIndex() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/columns/`, { timeout: 8000 }).then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SeoHead
        title="Newseze Columns — Five Daily Voices"
        description="Ask Mr Trask, Dr Love, Hollywood How Are You, Buyers Are Liars, and the Money-Maker of the Day — fact-checked daily columns from the Newseze editorial desk."
      />
      <Header />
      <main className="container mx-auto max-w-[1200px] px-4 py-6" data-testid="columns-index">
        <header className="mb-5">
          <div className="text-[11px] uppercase tracking-[0.22em] font-black text-[var(--gold)] mb-1">
            <BookOpen size={11} className="inline mr-1" /> Newseze Columns
          </div>
          <h1 className="text-[28px] sm:text-[36px] font-black tracking-[-0.02em] leading-tight">
            Daily voices, written for the way you actually live.
          </h1>
          <p className="text-[13.5px] text-[var(--muted)] mt-2 max-w-2xl">
            Five recurring columns from Newseze's editorial desk — fact-checked, scored, refreshed every morning at 6:30 a.m. local time.
          </p>
        </header>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data?.columns || []).map((c) => (
            <li key={c.slug} data-testid={`column-tile-${c.slug}`}>
              <Link to={`/columns/${c.slug}`} className="block rounded-xl border border-[var(--line)] bg-white p-4 hover:border-[var(--gold)] transition">
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--gold)] mb-1">{c.byline}</div>
                <h3 className="text-[19px] font-black tracking-[-0.01em] leading-tight">{c.title}</h3>
                <p className="text-[12.5px] text-[var(--muted)] mt-1 leading-snug">{c.tagline}</p>
                {c.preview && <p className="text-[12.5px] text-[var(--ink)] mt-2 italic line-clamp-2">"{c.preview}"</p>}
                <div className="flex items-center text-[11px] font-black mt-3 text-[var(--blue)]">
                  Read today's column <ChevronRight size={11} className="ml-1" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>
      <Footer />
    </>
  );
}

export default function ColumnPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    axios.get(`${API}/columns/${slug}`, { timeout: 90000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Could not load this column."); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const col = data?.column;
  const paras = (col?.body_markdown || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <>
      {col && (
        <SeoHead
          title={col.headline || col.title}
          description={col.deck || col.tagline || ""}
        />
      )}
      <Header />
      <main className="container mx-auto max-w-[820px] px-4 py-6" data-testid={`column-page-${slug}`}>
        <Link to="/columns" className="text-[12px] uppercase tracking-[0.16em] font-black text-[var(--blue)] hover:underline">
          ← All columns
        </Link>

        {busy && (
          <div className="text-[var(--muted)] flex items-center gap-2 text-[13px] py-6">
            <Loader2 size={14} className="animate-spin" /> Loading today's column…
          </div>
        )}
        {err && (
          <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 p-3 text-[13px] mt-4" data-testid="column-error">{err}</div>
        )}

        {col && (
          <article className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.22em] font-black text-[var(--gold)] mb-1">{col.byline}</div>
            <h1 className="text-[30px] sm:text-[38px] font-black tracking-[-0.02em] leading-tight" data-testid="column-headline">
              {col.headline || col.title}
            </h1>
            {col.deck && <p className="text-[15px] text-[var(--muted)] mt-2 leading-snug" data-testid="column-deck">{col.deck}</p>}

            {col.verdict_label && (
              <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-md bg-[var(--ink)] text-white text-[11px] uppercase tracking-[0.18em] font-black" data-testid="column-verdict">
                <Sparkles size={11} /> {col.verdict_label}
              </div>
            )}
            {typeof col.score === "number" && (
              <div className="inline-flex items-center gap-2 mt-3 ml-2 px-3 py-1.5 rounded-md bg-[var(--gold)] text-[#1d1303] text-[11px] uppercase tracking-[0.18em] font-black" data-testid="column-score">
                <TrendingUp size={11} /> {col.score_label || "Score"} · {col.score}
              </div>
            )}

            <div className="mt-5 space-y-4 text-[15.5px] leading-[1.65] text-[var(--ink)]">
              {paras.map((p, i) => (
                <p key={i} data-testid={`column-para-${i}`}>{p}</p>
              ))}
            </div>

            {Array.isArray(col.action_items) && col.action_items.length > 0 && (
              <div className="mt-6 rounded-xl border-2 border-[var(--gold)] bg-[#fff7d6] p-4" data-testid="column-actions">
                <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[#1d1303] mb-2">Action items</div>
                <ol className="space-y-1.5 list-decimal pl-5">
                  {col.action_items.map((a, i) => (
                    <li key={i} className="text-[13.5px] font-bold text-[#1d1303]" data-testid={`column-action-${i}`}>{a}</li>
                  ))}
                </ol>
              </div>
            )}

            <p className="text-[10.5px] text-[var(--muted)] italic mt-6 border-t border-[var(--line)] pt-3">
              {col.legal_footer} · Column refreshed daily {col.generated_at && `· Generated ${new Date(col.generated_at).toLocaleString()}`}
            </p>
          </article>
        )}
      </main>
      <Footer />
    </>
  );
}
